import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, PhoneIcon, ArrowLeftIcon, ArrowRightIcon, XIcon, UserIcon, CameraIcon, UploadCloudIcon, ShieldCheckIcon, CheckCircle2Icon, ChevronDownIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { Logo } from './Logo';
type DialCode = {
  iso: string;
  dial: string;
  name: string;
};
// UAE first, then other GCC markets.
const DIAL_CODES: DialCode[] = [{
  iso: 'ae',
  dial: '+971',
  name: 'United Arab Emirates'
}, {
  iso: 'sa',
  dial: '+966',
  name: 'Saudi Arabia'
}, {
  iso: 'qa',
  dial: '+974',
  name: 'Qatar'
}, {
  iso: 'kw',
  dial: '+965',
  name: 'Kuwait'
}, {
  iso: 'bh',
  dial: '+973',
  name: 'Bahrain'
}, {
  iso: 'om',
  dial: '+968',
  name: 'Oman'
}];
function CountryFlag({
  iso
}: {
  iso: string;
}) {
  return <img src={`https://flagcdn.com/w40/${iso}.png`} srcSet={`https://flagcdn.com/w80/${iso}.png 2x`} width={20} height={15} alt="" aria-hidden="true" className="rounded-[3px] object-cover shadow-sm ring-1 ring-black/5" data-id="element-1923" />;
}
// Phone input with a separated, selectable country dial-code prefix.
function PhoneField({
  value,
  onChange,
  dialCode,
  onDialCodeChange
}: {
  value: string;
  onChange: (v: string) => void;
  dialCode: DialCode;
  onDialCodeChange: (d: DialCode) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return <div className="flex items-stretch gap-2" data-id="element-1924">
      {/* Dial-code prefix */}
      <div className="relative" ref={ref} data-id="element-1925">
        <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={`h-full flex items-center gap-2 px-3 border rounded-xl transition-colors ${open ? 'border-primary-indigo bg-primary-indigo/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-id="element-1926">
          <CountryFlag iso={dialCode.iso} data-id="element-1927" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-1928">
            {dialCode.dial}
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180 text-primary-indigo' : ''}`} data-id="element-1929" />
        </button>
        <AnimatePresence data-id="element-1930">
          {open && <motion.div role="listbox" initial={{
          opacity: 0,
          y: -6,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -6,
          scale: 0.97
        }} transition={{
          duration: 0.15
        }} className="absolute left-0 mt-2 w-64 bg-white border border-slate-100 rounded-2xl shadow-[0_16px_48px_-8px_rgb(0,0,0,0.18)] overflow-hidden z-50 p-1.5 space-y-0.5" data-id="element-1931">
              {DIAL_CODES.map(d => {
            const active = d.iso === dialCode.iso;
            return <button key={d.iso} type="button" role="option" aria-selected={active} onClick={() => {
              onDialCodeChange(d);
              setOpen(false);
            }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? 'bg-primary-indigo/[0.06]' : 'hover:bg-slate-50'}`} data-id="element-1932">
                    <CountryFlag iso={d.iso} data-id="element-1933" />
                    <span className="flex-1 min-w-0" data-id="element-1934">
                      <span className={`block text-sm truncate ${active ? 'font-semibold text-primary-indigo' : 'font-medium text-slate-800'}`} data-id="element-1935">
                        {d.name}
                      </span>
                    </span>
                    <span className={`text-sm font-semibold ${active ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-1936">
                      {d.dial}
                    </span>
                    {active && <CheckIcon className="w-4 h-4 text-primary-indigo shrink-0" data-id="element-1937" />}
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>
      {/* Local number */}
      <input type="tel" value={value} onChange={e => onChange(e.target.value)} placeholder="50 123 4567" className="flex-1 min-w-0 px-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-1938" />
    </div>;
}
type Step = 'method' | 'email' | 'phone' | 'otp' | 'profile' | 'verify';
interface LoginProps {
  onLogin: () => void;
  onClose?: () => void;
}
function GoogleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1939">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" data-id="element-1940" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" data-id="element-1941" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" data-id="element-1942" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" data-id="element-1943" />
    </svg>;
}
function AppleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-1944">
      <path d="M16.36 12.78c-.02-2.05 1.67-3.03 1.75-3.08-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.62-.72-2.67-.7-1.37.02-2.64.8-3.35 2.03-1.43 2.48-.37 6.15 1.02 8.16.68.98 1.49 2.08 2.55 2.04 1.02-.04 1.41-.66 2.65-.66 1.23 0 1.58.66 2.66.64 1.1-.02 1.79-1 2.46-1.99.78-1.14 1.1-2.24 1.12-2.3-.02-.01-2.15-.83-2.18-3.27zM14.3 6.58c.56-.68.94-1.62.84-2.58-.81.03-1.79.54-2.37 1.22-.52.6-.98 1.56-.86 2.48.9.07 1.83-.46 2.39-1.12z" data-id="element-1945" />
    </svg>;
}
export function Login({
  onLogin,
  onClose
}: LoginProps) {
  const [step, setStep] = useState<Step>('method');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState<DialCode>(DIAL_CODES[0]);
  const [fullName, setFullName] = useState('');
  const [idStatus, setIdStatus] = useState<'idle' | 'uploaded'>('idle');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const goToOtp = () => setStep('otp');
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const otpComplete = otp.every(d => d !== '');
  const stepVariants = {
    enter: {
      opacity: 0,
      x: 24
    },
    center: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -24
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-id="element-1946">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-1947" />
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="relative w-full max-w-md bg-white rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.18)] border border-slate-100 p-8 z-10" data-id="element-1948">
        {onClose && <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1949">
            <XIcon className="w-5 h-5" data-id="element-1950" />
          </button>}
        <div className="flex justify-center mb-6" data-id="element-1951">
          <Logo size="md" data-id="element-1952" />
        </div>

        <AnimatePresence mode="wait" data-id="element-1953">
          {/* METHOD SELECTION */}
          {step === 'method' && <motion.div key="method" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1954">
              <div className="text-center mb-7" data-id="element-1955">
                <h1 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-1956">
                  Welcome to Talab
                </h1>
                <p className="text-slate-500 text-sm" data-id="element-1957">
                  Sign in to let your AI agent find anything for you.
                </p>
              </div>

              <div className="space-y-3" data-id="element-1958">
                <button onClick={() => setStep('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1959">
                  <GoogleMark data-id="element-1960" />
                  Continue with Google
                </button>
                <button onClick={() => setStep('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 bg-dark-navy text-white rounded-xl font-medium hover:opacity-90 transition-opacity" data-id="element-1961">
                  <AppleMark data-id="element-1962" />
                  Continue with Apple
                </button>

                <div className="flex items-center gap-3 py-2" data-id="element-1963">
                  <div className="flex-1 h-px bg-slate-100" data-id="element-1964" />
                  <span className="text-xs text-slate-400 font-medium" data-id="element-1965">OR</span>
                  <div className="flex-1 h-px bg-slate-100" data-id="element-1966" />
                </div>

                <button onClick={() => setStep('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1967">
                  <PhoneIcon className="w-5 h-5 text-primary-indigo" data-id="element-1968" />
                  Continue with Phone
                </button>
                <button onClick={() => setStep('email')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1969">
                  <MailIcon className="w-5 h-5 text-primary-indigo" data-id="element-1970" />
                  Continue with Email
                </button>
              </div>

              <p className="text-center text-xs text-slate-400 mt-6 leading-relaxed" data-id="element-1971">
                By continuing you agree to Talab's Terms of Service and Privacy
                Policy.
              </p>
            </motion.div>}

          {/* EMAIL STEP */}
          {step === 'email' && <motion.div key="email" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1972">
              <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-1973">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1974" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1975">
                Sign in with email
              </h1>
              <p className="text-slate-500 text-sm mb-6" data-id="element-1976">
                We'll also verify your phone number to secure your account.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1977">
                Email address
              </label>
              <div className="relative mb-4" data-id="element-1978">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1979" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-1980" />
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1981">
                Phone number <span className="text-error" data-id="element-1982">*</span>
              </label>
              <div className="mb-6" data-id="element-1983">
                <PhoneField value={phone} onChange={setPhone} dialCode={dialCode} onDialCodeChange={setDialCode} data-id="element-1984" />
                <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1" data-id="element-1985">
                  <MessageCircleIcon className="w-3.5 h-3.5 text-[#25D366]" data-id="element-1986" />
                  Required — we'll send a one-time code via WhatsApp to verify
                  it.
                </p>
              </div>

              <button onClick={goToOtp} disabled={!email.includes('@') || phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1987">
                Send verification code
                <ArrowRightIcon className="w-4 h-4" data-id="element-1988" />
              </button>
            </motion.div>}

          {/* PHONE STEP */}
          {step === 'phone' && <motion.div key="phone" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1989">
              <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-1990">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1991" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1992">
                Verify your phone
              </h1>
              <p className="text-slate-500 text-sm mb-6 flex items-center gap-1.5" data-id="element-1993">
                <MessageCircleIcon className="w-4 h-4 text-[#25D366] shrink-0" data-id="element-1994" />
                Enter your phone number and we'll send a one-time code via
                WhatsApp.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1995">
                Phone number <span className="text-error" data-id="element-1996">*</span>
              </label>
              <div className="mb-6" data-id="element-1997">
                <PhoneField value={phone} onChange={setPhone} dialCode={dialCode} onDialCodeChange={setDialCode} data-id="element-1998" />
              </div>

              <button onClick={goToOtp} disabled={phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1999">
                Send verification code
                <ArrowRightIcon className="w-4 h-4" data-id="element-2000" />
              </button>
            </motion.div>}

          {/* OTP STEP */}
          {step === 'otp' && <motion.div key="otp" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2001">
              <button onClick={() => setStep(email ? 'email' : 'phone')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-2002">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2003" />
                Back
              </button>
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2004">
                Enter verification code
              </h1>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-full bg-[#25D366]/10 text-[#1da851] text-xs font-medium" data-id="element-2005">
                <MessageCircleIcon className="w-3.5 h-3.5" data-id="element-2006" />
                Sent via WhatsApp
              </div>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2007">
                We sent a 6-digit code on WhatsApp to{' '}
                <span className="font-medium text-slate-700" data-id="element-2008">
                  {phone ? `${dialCode.dial} ${phone}` : 'your phone'}
                </span>
                .
              </p>

              <div className="flex justify-between gap-2 mb-6" data-id="element-2009">
                {otp.map((digit, i) => <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} aria-label={`Digit ${i + 1}`} className="w-12 h-14 text-center text-xl font-bold border border-slate-200 rounded-xl outline-none focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/20 transition-all" data-id="element-2010" />)}
              </div>

              <button onClick={() => setStep('profile')} disabled={!otpComplete} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2011">
                Verify & continue
              </button>

              <p className="text-center text-sm text-slate-500 mt-5" data-id="element-2012">
                Didn't get the code?{' '}
                <button className="font-medium text-primary-indigo hover:underline" data-id="element-2013">
                  Resend
                </button>
              </p>
            </motion.div>}

          {/* PROFILE COMPLETION STEP */}
          {step === 'profile' && <motion.div key="profile" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2014">
              <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2015">
                Confirm your details
              </h1>
              <p className="text-slate-500 text-sm mb-6" data-id="element-2016">
                Let's make sure we have your name and email right.
              </p>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2017">
                Full name <span className="text-error" data-id="element-2018">*</span>
              </label>
              <div className="relative mb-4" data-id="element-2019">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2020" />
                <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Ahmed Al Mansoori" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2021" />
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2022">
                Email address <span className="text-error" data-id="element-2023">*</span>
              </label>
              <div className="relative mb-6" data-id="element-2024">
                <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-2025" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-2026" />
              </div>

              <button onClick={() => setStep('verify')} disabled={fullName.trim().length < 2 || !email.includes('@')} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2027">
                Continue
                <ArrowRightIcon className="w-4 h-4" data-id="element-2028" />
              </button>
            </motion.div>}

          {/* ID VERIFICATION STEP */}
          {step === 'verify' && <motion.div key="verify" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-2029">
              <div className="flex flex-col items-center text-center mb-6" data-id="element-2030">
                <div className="w-14 h-14 rounded-2xl bg-primary-indigo/10 flex items-center justify-center mb-4" data-id="element-2031">
                  <ShieldCheckIcon className="w-7 h-7 text-primary-indigo" data-id="element-2032" />
                </div>
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-2033">
                  Verify your identity
                </h1>
                <p className="text-slate-500 text-sm" data-id="element-2034">
                  Scan or upload your ID. We rely on a verified ID to keep Talab
                  safe and unlock full access.
                </p>
              </div>

              {idStatus === 'uploaded' ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl mb-6" data-id="element-2035">
                  <CheckCircle2Icon className="w-6 h-6 text-success shrink-0" data-id="element-2036" />
                  <div className="text-left" data-id="element-2037">
                    <p className="font-medium text-slate-900 text-sm" data-id="element-2038">
                      ID submitted
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2039">
                      We'll review it shortly — you can continue in the
                      meantime.
                    </p>
                  </div>
                </div> : <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-2040">
                  <button onClick={() => setIdStatus('uploaded')} className="flex flex-col items-center justify-center p-5 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-2041">
                    <CameraIcon className="w-7 h-7 text-slate-400 group-hover:text-primary-indigo mb-2 transition-colors" data-id="element-2042" />
                    <span className="font-medium text-sm text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-2043">
                      Scan ID
                    </span>
                  </button>
                  <button onClick={() => setIdStatus('uploaded')} className="flex flex-col items-center justify-center p-5 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-2044">
                    <UploadCloudIcon className="w-7 h-7 text-slate-400 group-hover:text-primary-indigo mb-2 transition-colors" data-id="element-2045" />
                    <span className="font-medium text-sm text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-2046">
                      Upload ID
                    </span>
                  </button>
                </div>}

              <button onClick={onLogin} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors" data-id="element-2047">
                {idStatus === 'uploaded' ? 'Finish & continue' : 'Continue'}
              </button>
              {idStatus !== 'uploaded' && <button onClick={onLogin} className="w-full mt-2 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors" data-id="element-2048">
                  Verify later
                </button>}
            </motion.div>}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
}